import { z } from "zod";

const TYPE_LABEL_PT_BR: Record<string, string> = {
  string: "texto",
  number: "número",
  bigint: "número grande",
  boolean: "verdadeiro/falso",
  date: "data",
  array: "lista",
  object: "objeto",
  map: "mapa",
  set: "conjunto",
  function: "função",
  symbol: "símbolo",
  undefined: "vazio",
  null: "vazio",
  void: "vazio",
  never: "nunca",
  any: "qualquer",
  unknown: "desconhecido",
  nan: "NaN",
};

function labelOfType(t: string | undefined | null): string {
  if (!t) return "valor";
  return TYPE_LABEL_PT_BR[t] ?? t;
}

export const PT_BR_ZOD_ERROR_MAP: z.ZodErrorMap = (issue, ctx) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === "undefined" || issue.received === "null") {
        return { message: "Campo obrigatório." };
      }
      return {
        message: `Esperado ${labelOfType(issue.expected)}, recebido ${labelOfType(issue.received)}.`,
      };

    case z.ZodIssueCode.too_small:
      if (issue.type === "string") {
        if (issue.minimum === 1) return { message: "Campo obrigatório." };
        return { message: `Mínimo ${issue.minimum} caracteres.` };
      }
      if (issue.type === "number" || issue.type === "bigint") {
        const op = issue.inclusive ? "≥" : ">";
        return { message: `Valor deve ser ${op} ${issue.minimum}.` };
      }
      if (issue.type === "array") {
        return { message: `Selecione ao menos ${issue.minimum} item(s).` };
      }
      if (issue.type === "date") {
        const minDate = new Date(Number(issue.minimum));
        return { message: `Data mínima: ${minDate.toLocaleDateString("pt-BR")}.` };
      }
      break;

    case z.ZodIssueCode.too_big:
      if (issue.type === "string") {
        return { message: `Máximo ${issue.maximum} caracteres.` };
      }
      if (issue.type === "number" || issue.type === "bigint") {
        const op = issue.inclusive ? "≤" : "<";
        return { message: `Valor deve ser ${op} ${issue.maximum}.` };
      }
      if (issue.type === "array") {
        return { message: `Máximo ${issue.maximum} item(s).` };
      }
      if (issue.type === "date") {
        const maxDate = new Date(Number(issue.maximum));
        return { message: `Data máxima: ${maxDate.toLocaleDateString("pt-BR")}.` };
      }
      break;

    case z.ZodIssueCode.invalid_string:
      if (typeof issue.validation === "string") {
        if (issue.validation === "email") return { message: "E-mail inválido." };
        if (issue.validation === "url") return { message: "URL inválida." };
        if (issue.validation === "uuid") return { message: "Identificador inválido." };
        if (issue.validation === "cuid") return { message: "Identificador inválido." };
        if (issue.validation === "datetime") return { message: "Data/hora inválida." };
        if (issue.validation === "regex") return { message: "Formato inválido." };
      }
      return { message: "Texto inválido." };

    case z.ZodIssueCode.invalid_enum_value:
      return {
        message: `Valor inválido. Esperado: ${issue.options.join(", ")}.`,
      };

    case z.ZodIssueCode.invalid_date:
      return { message: "Data inválida." };

    case z.ZodIssueCode.not_finite:
      return { message: "Número inválido (não pode ser infinito)." };

    case z.ZodIssueCode.not_multiple_of:
      return { message: `Valor deve ser múltiplo de ${issue.multipleOf}.` };

    case z.ZodIssueCode.unrecognized_keys:
      return { message: `Campos não reconhecidos: ${issue.keys.join(", ")}.` };

    case z.ZodIssueCode.invalid_union:
      return { message: "Valor não corresponde a nenhuma das opções esperadas." };

    case z.ZodIssueCode.invalid_union_discriminator:
      return {
        message: `Discriminador inválido. Esperado: ${issue.options.join(", ")}.`,
      };

    case z.ZodIssueCode.invalid_arguments:
      return { message: "Argumentos de função inválidos." };

    case z.ZodIssueCode.invalid_return_type:
      return { message: "Tipo de retorno inválido." };

    case z.ZodIssueCode.invalid_intersection_types:
      return { message: "Valores de interseção incompatíveis." };

    case z.ZodIssueCode.custom:
      return { message: ctx.defaultError };
  }

  return { message: ctx.defaultError };
};

export function registerZodPtBR(): void {
  z.setErrorMap(PT_BR_ZOD_ERROR_MAP);
}
